import { computed, defineComponent, ref, type FunctionalComponent, watch, type VNode } from 'vue'
import type { ISelectInputProps } from './types'
import { classPairForVariant } from '@/utils/style'
import type { IInputOption } from '../input/types'
import Icon from 'components/icon'
import { toString } from '@/utils/formatting'
import type { IRectangle } from '@/types/geometry'
import ChevronDownIcon from 'assets/img/svg/icons/chevron_down.svg?url'
import { clamp } from '@/utils/math'
import type { IBaseComponentProps } from 'components/base/types'
import { isNullish } from '@/utils/is'
import { tv } from 'tailwind-variants'
import Tooltip from 'components/tooltip'
import { onClickOutside } from '@vueuse/core'

const DEFAULT_HEIGHT = 36

const VERTICAL_PADDING = 8

const OptionItemClass = tv({
  base: 'p-3 cursor-pointer pointer-events-auto flex flex-row justify-between items-center gap-2 w-full relative select-none flex-1 hover:bg-gray-100',
  variants: {
    disabled: {
      true: 'opacity-[0.25] cursor-default'
    },
    selected: {
      true: 'bg-primary-50 text-primary-500'
    }
  }
})

const SelectInput = defineComponent<ISelectInputProps>({
  name: 'SelectInput',
  setup(props) {
    const initialRect = ref<IRectangle>({
      x: 0,
      y: 0,
      width: 0,
      height: 0
    });

    const propValue = computed(() => unref(props.value));

    const vn = ref<VNode | undefined>(undefined)

    // const currentEl = useCurrentElement()

    //const watcher = watch(
    //  currentEl,
    //  (e) => {
    //    if (e) {
    //      const rect = e.getBoundingClientRect()
    //      console.log({ rect });
    //      if (rect.height > 1) {
    //        initialRect.value = rect
    //        watcher()
    //      }
    //    }
    //  },
    //  { immediate: true, deep: true }
    //);

    let observer: ResizeObserver | undefined = undefined

    const handleMount = (node: VNode) => {
      vn.value = node
      if (!node.el) return
      const el = node.el as HTMLElement
      initialRect.value = el.getBoundingClientRect()
      observer = new ResizeObserver((c, o) => {
        for (const x of c) {
          if (x.contentRect.height > 1) {
            initialRect.value = x.contentRect
            o.disconnect()
            break
          }
        }
      })

      observer.observe(el)
    }

    // const itemHeight = computed((): number => {
    //   const h = (() => {
    //     if (initialRect.value.height > 1) return Math.max(initialRect.value.height, DEFAULT_HEIGHT)
    //     return DEFAULT_HEIGHT + VERTICAL_PADDING
    //   })()

    //   const m = DEFAULT_HEIGHT + VERTICAL_PADDING
    //   return clamp(h, m, m * 2)
    // })

    const expandedHeight = computed((): number => {
      console.log(DEFAULT_HEIGHT + VERTICAL_PADDING, props.options?.maxVisibleItems)
      return (DEFAULT_HEIGHT + VERTICAL_PADDING) * (props.options?.maxVisibleItems || 1)
    })

    const expanded = ref<boolean>(props.expanded || false)
    const componentRef = ref(null)

    onClickOutside(componentRef, () => {
      expanded.value = false;
    });

    // const onClickOutside = (event) => {
    //   if (componentRef.value && !componentRef.value.contains(event.target)) {
    //     expanded.value = false;
    //   }
    // };

    // Temp disabled since it is not working when utilizing the component without the input field.
    // e.g. In navbar, Avatar component is used as button and on click it opens the dropdown.
    // This is not working with this approach to close the dropdown on outside click.
    // onMounted(() => {
    //   console.log('HALLÅ')
    //   window.addEventListener('click', onClickOutside);
    // });

    // onUnmounted(() => {
    //   console.log('HEJ DÅ')
    //   window.removeEventListener('click', onClickOutside);
    // });

    const items = computed(() => {
      const opts = (() => {
        if (!props.options || !props.options.items || props.options.items.length <= 0) return []
        return props.options.items
      })()
      if (opts.length <= 0) return []

      if (props.placeholder && isNullish(propValue.value)) {
        return [
          {
            label: toString(props.placeholder),
            value: undefined,
            icon: props.placeholderIcon,
            selected: true,
            isPlaceholder: true
          },
          ...opts
        ]
      }

      return opts
    })

    const getInitialIndex = (): number => {
      if (!props.options || !items.value || items.value.length <= 0) return 0
      if (isNullish(propValue.value)) return 0
      let opt = items.value.find((it) => it.selected)
      if (!opt && propValue.value) {
        opt = items.value.find((it) => it.value === propValue.value)
      }
      if (!opt) return 0
      return items.value.indexOf(opt)
    }

    const selectedIndex = ref<number>(getInitialIndex())

    watch(props, (_d) => {
      if (props.disabled) {
        selectedIndex.value = 0
      }
    })

    const setSelectedIndex = (i: number) => {
      selectedIndex.value = i
    }

    const toggleExpanded = () => {
      expanded.value = !expanded.value
    }

    const handleOptionClick = (option: IInputOption, i: number) => {
      if (props.disabled || option.disabled) return

      if (!propValue.value && props.placeholder) i = Math.max(0, i - 1)

      if (option.action) {
        option.action(option)
      }

      if (expanded.value) {
        setSelectedIndex(i)

        if (props.fieldRef) {
          if (props.fieldRef.value.options && props.fieldRef.value.options.items) {
            props.fieldRef.value.options.items = props.fieldRef.value.options.items.map(
              (it, j) => ({ ...it, selected: j === i })
            )
          }

          props.fieldRef.value.value = option.value

          if (props.reportChange) {
            props.reportChange(props.fieldRef)
          }
        }
      }
      toggleExpanded()
    }

    const selectedOption = computed(() => {
      if (!items.value || items.value.length <= 0) return undefined
      if (isNullish(propValue.value) && props.placeholder) return items.value[0]
      return items.value[clamp(selectedIndex.value, 0, items.value.length)]
    })

    return () => {
      const { bg: bgClass, fg: fgClass } = classPairForVariant(props.variant || 'white')

      interface OptionItemProps extends IBaseComponentProps {
        option: IInputOption
        i: number
        selected: boolean
        placeholderClass?: string | string[]
      }

      const OptionItem: FunctionalComponent<OptionItemProps> = (props) => {
        const { option } = props
        const displayText = toString(option.label || option.name || option.value)
        return (
          <Tooltip text={option.tooltip} disabled={!option.tooltip} placement="top">
            <div
              class={OptionItemClass({
                disabled: option.disabled,
                selected: selectedOption.value?.value === option.value
              })}
              role="option"
              tab-index="-1"
            >
              {option.icon && <Icon src={option.icon} fill="currentColor" />}
              {option.image && <option.image />}
              <span class="flex flex-1 truncate text-sm font-medium">{displayText}</span>
            </div>
          </Tooltip>
        )
      }

      const ItemList: FunctionalComponent = () => {
        return (
          <div
            {...{ onVnodeMounted: handleMount }}
            class={[
              'select-input pretty-scrollbar',
              'bg-white',
              bgClass,
              fgClass,
              'rounded-sm shadow-lg',
              'absolute left-0 right-0 z-50'
            ]}
            style={{
              top: '100%',
              maxHeight: `${expandedHeight.value}px`,
              overflowY: 'auto',
              width: '100%'
            }}
          >
            {(items.value || []).map((option, i) => (
              <OptionItem
                selected={selectedIndex.value === i}
                option={option}
                placeholderClass={props.placeholderClass}
                onClick={() => handleOptionClick(option, i)}
                i={i}
              />
            ))}
          </div>
        )
      }

      return (
        <div
          ref={componentRef}
          class={[
            'relative w-full',
            props.hiddenWhenCollapsed
              ? []
              : [!props.textOnly ? `${bgClass} border` : '', fgClass, 'py-2']
          ]}
          style={{
            height: `${DEFAULT_HEIGHT + VERTICAL_PADDING}px`,
            opacity: props.disabled ? '60%' : '100%'
          }}
        >
          <div class="flex flex-1 w-full h-full px-2.5 cursor-pointer items-center" onClick={toggleExpanded}>
            {selectedOption.value && (
              <div class="flex flex-row flex-1 justify-between items-center text-sm w-full">
                <div class="flex flex-row items-center gap-2 flex-1 min-w-0 mr-2">
                  {props.selectedIcon && <Icon src={props.selectedIcon} fill="currentColor" class="shrink-0" />}
                  {!props.selectedIcon && selectedOption.value.icon && (
                    <Icon src={selectedOption.value.icon} fill="currentColor" class="shrink-0" />
                  )}
                  {!props.selectedIcon && !selectedOption.value.icon && selectedOption.value.image && (
                    <selectedOption.value.image />
                  )}
                  <div
                    class={`truncate ${selectedOption.value.label === props.placeholder ? props.placeholderClass : ''}`}
                  >
                    {selectedOption.value.label}
                  </div>
                </div>
                <Icon src={ChevronDownIcon} fill="currentColor" class="shrink-0" />
              </div>
            )}
            {!selectedOption.value && props.placeholder && (
              <div class="flex flex-row flex-1 justify-between items-center text-sm w-full">
                <div class="flex flex-row items-center gap-2 flex-1 min-w-0 mr-2">
                  {props.placeholderIcon && (
                    <Icon src={props.placeholderIcon} fill="currentColor" class="shrink-0" />
                  )}
                  <div
                    class={`truncate ${props.placeholderClass}`}
                  >
                    {props.placeholder}
                  </div>
                </div>
                <Icon src={ChevronDownIcon} fill="currentColor" class="shrink-0 ml-2" />
              </div>
            )}
          </div>
          {expanded.value && <ItemList />}
        </div>
      )
    }
  }
})

SelectInput.props = [
  'value',
  'options',
  'selectedIcon',
  'variant',
  'onInput',
  'name',
  'hiddenWhenCollapsed',
  'expanded',
  'anchor',
  'fieldRef',
  'reportChange',
  'placeholder',
  'placeholderIcon',
  'placeholderClass',
  'disabled',
  'type',
  'textOnly'
]

export default SelectInput
